/**
 * UK Carbon Intensity API Client
 * Documentation: https://carbon-intensity.github.io/api-definitions/
 *
 * Free public API - no API key required
 * Provides half-hourly carbon intensity data for the UK grid
 */

const CARBON_INTENSITY_BASE_URL = 'https://api.carbonintensity.org.uk';

/**
 * Generic API fetch wrapper with error handling
 */
const fetchCarbonIntensity = async (endpoint) => {
  try {
    const response = await fetch(`${CARBON_INTENSITY_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Carbon Intensity API Error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error('Carbon Intensity API fetch error:', error);
    throw error;
  }
};

/**
 * Get current carbon intensity
 * Returns the current half-hour carbon intensity
 */
export const getCurrentIntensity = async () => {
  return fetchCarbonIntensity('/intensity');
};

/**
 * Get carbon intensity for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 */
export const getIntensityByDate = async (date) => {
  return fetchCarbonIntensity(`/intensity/date/${date}`);
};

/**
 * Get carbon intensity between two datetime points
 * @param {string} from - ISO datetime string
 * @param {string} to - ISO datetime string
 */
export const getIntensityRange = async (from, to) => {
  return fetchCarbonIntensity(`/intensity/${from}/${to}`);
};

/**
 * Get generation mix for a specific date
 * @param {string} date - Date in YYYY-MM-DD format
 * Returns breakdown by fuel type with percentages
 */
export const getGenerationMix = async (date) => {
  return fetchCarbonIntensity(`/generation/${date}/pt24h`);
};

/**
 * Get current generation mix
 */
export const getCurrentGenerationMix = async () => {
  return fetchCarbonIntensity('/generation');
};

/**
 * Get regional carbon intensity
 * @param {string} regionId - Optional region ID (1-17)
 */
export const getRegionalIntensity = async (regionId) => {
  if (regionId) {
    return fetchCarbonIntensity(`/regional/regionid/${regionId}`);
  }
  return fetchCarbonIntensity('/regional');
};

/**
 * Get intensity statistics for a date range
 * @param {string} from - Start date (YYYY-MM-DD)
 * @param {string} to - End date (YYYY-MM-DD)
 */
export const getIntensityStats = async (from, to) => {
  return fetchCarbonIntensity(`/intensity/stats/${from}/${to}`);
};

/**
 * Get intensity factors (what affects carbon intensity)
 */
export const getIntensityFactors = async () => {
  return fetchCarbonIntensity('/intensity/factors');
};

// Export all functions
const carbonIntensityApi = {
  getCurrentIntensity,
  getIntensityByDate,
  getIntensityRange,
  getGenerationMix,
  getCurrentGenerationMix,
  getRegionalIntensity,
  getIntensityStats,
  getIntensityFactors,
};

export default carbonIntensityApi;
